import { GridColDef } from "@mui/x-data-grid";
import { Drawer } from "components/Drawer";
import DataGrid from "components/table/DataGrid";
import EditAgentModal from "components/agent/EditAgentModal";
import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchAgentsAsync, selectAgents } from '../../app/services/agents/agentSlice';
import { IAgent } from "definitions/app/agent";
import {
  Box, Typography, alpha, useTheme, useMediaQuery,
  Stack, Chip, CircularProgress,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupsIcon from "@mui/icons-material/Groups";

export const Agents = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const { agents, status } = useAppSelector(selectAgents);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editItem, setEditItem] = useState<IAgent | null>(null);

  const columns = useMemo<GridColDef<IAgent>[]>(
    () => [
      { field: "name", headerName: "Name", flex: 1 },
      { field: "mobile", headerName: "Mobile", flex: 1 },
      { field: "address", headerName: "Address", flex: 1 },
    ],
    []
  );

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      dispatch(fetchAgentsAsync());
    }
  }, [dispatch]);

  const reset = useCallback(() => {
    setOpenDrawer(false);
    setEditItem(null);
  }, []);

  const isLoading = status === "loading";
  const activeCount = agents.filter((a) => a.active).length;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 3.5 },
          display: "flex",
          alignItems: "center",
          borderRadius: 1,
          gap: 2,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
        }}
      >
        <Box sx={{ bgcolor: alpha("#fff", 0.15), borderRadius: 2, p: 1, display: "flex", alignItems: "center" }}>
          <SupportAgentIcon sx={{ color: "#fff", fontSize: { xs: 28, sm: 36 } }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
            Agents
          </Typography>
          <Typography variant="body2" sx={{ color: alpha("#fff", 0.8), mt: 0.3 }}>
            View all registered delivery agents
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip
            icon={<GroupsIcon sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
            label={`${agents.length} Total`}
            size="small"
            sx={{ bgcolor: alpha("#fff", 0.2), color: "#fff", fontWeight: 600, fontSize: "0.7rem" }}
          />
          <Chip
            label={`${activeCount} Active`}
            size="small"
            sx={{ bgcolor: alpha("#fff", 0.2), color: "#fff", fontWeight: 600, fontSize: "0.7rem" }}
          />
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiPaper-root": { boxShadow: "none !important" },
            }}
          >
            <DataGrid
              title="Jaman HP Gas | Agents"
              columns={columns}
              rows={agents}
            />
          </Box>
        )}
      </Box>

      <Drawer
        title="Update Agent"
        variant="lg"
        anchor="right"
        open={openDrawer}
        onClose={() => reset()}
      >
        <EditAgentModal
          open={openDrawer}
          onClose={() => reset()}
          agent={editItem}
          onUpdate={() => {
            reset();
            dispatch(fetchAgentsAsync());
          }}
        />
      </Drawer>
    </Box>
  );
};
